/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BusinessSettingsModal} from '../business-settings-modal';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {PartnerModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/partner.model';
import {ValidatorFn, Validators} from '@angular/forms';
import {TooltipEnum} from '../../../../shared/tooltip/tooltip.enum';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {take} from 'rxjs/operators';
import {timer} from 'rxjs';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {InputListModel} from '../input-list.model';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';

@Component({
    selector: 'app-partner-modal',
    templateUrl: './partner-modal.component.html',
    styleUrls: ['../business-settings-modal.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartnerModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<PartnerModel>> {

    @ViewChild('name') nameInput: InputTextComponent;
    @ViewChild('code') codeInput: InputTextComponent;
    @ViewChild('sequencer') sequencerInput: InputTextComponent;

    readonly EDIT_TITLE = 'Edit partner';
    readonly ADD_TITLE = 'Add a partner';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';
    readonly ENTITY_NAME = 'partner';

    validators: { [key: string]: ValidatorFn[] } = {
        name: [Validators.pattern(/^[\w-/\s\\.(),]{1,100}$/)],
        code: [Validators.pattern(/^\d{10}$/)],
        sequencer: [Validators.pattern(/^\d{2}$/)]
    };

    TooltipEnum = TooltipEnum;

    public nameMessages = new Map<WarningsEnum, string[]>();
    public codeMessages = new Map<WarningsEnum, string[]>();
    public sequencerMessages = new Map<WarningsEnum, string[]>();

    constructor(public typeahead: TypeaheadHelperService, inputMessages: InputMessagesService) {
        super(inputMessages);
    }

    setEntity(element: EntityElementModel<PartnerModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: {...element.currentValue, displayParentCode: element.displayParentCode}
        });
        this.onReady.pipe(take(1))
.subscribe(() => {
            timer(0)
.subscribe(() => {
                this.nameInput.setValue(this.state.currentValue.name);
                this.codeInput.setValue(this.state.currentValue.code);
                this.sequencerInput.setValue(this.state.currentValue.sequencer);
                this.setActiveInputState(element.active, true);
            });
        });
    }

    nameInputChange(name: string): void {
        this.inputMessages.mandatoryWarnings(this.nameInput.inputControl, this.nameMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(this.nameInput.inputControl, this.nameMessages, this.nameInput.label);
        this.nameMessages = new Map(this.nameMessages);
        const errors = this.nameMessages.get(WarningsEnum.ERROR);

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                name: name
            },
            invalid: {
                ...this.state.invalid,
                name: (name == null || name.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    codeInputChange(code: string): void {
        this.inputMessages.mandatoryWarnings(this.codeInput.inputControl, this.codeMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.codeInput.inputControl,
                this.codeMessages,
                this.codeInput.label,
                '1300049288'
        );

        this.codeMessages = new Map(this.codeMessages);

        const errors = this.codeMessages.get(WarningsEnum.ERROR);

        if (errors.length === 0) {
            this.manageAlreadyExistsCode(code, this.ENTITY_NAME, this.codeMessages, this.codeInput);
        }

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: code
            },
            invalid: {
                ...this.state.invalid,
                code: (code == null || code.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    sequencerInputChange(sequencer: string): void {
        this.inputMessages.mandatoryWarnings(this.sequencerInput.inputControl, this.sequencerMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.sequencerInput.inputControl,
                this.sequencerMessages,
                this.sequencerInput.label,
                '05'
        );
        this.sequencerMessages = new Map(this.sequencerMessages);
        const errors = this.sequencerMessages.get(WarningsEnum.ERROR);

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                sequencer: sequencer
            },
            invalid: {
                ...this.state.invalid,
                sequencer: (sequencer == null || sequencer.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.nameInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.codeInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.sequencerInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }];
    }
}
